import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import Modal from "@/components/Modal";
import { Badge } from "@/components/ui/badge";
import { useDebounce } from "@/hooks/useDebounce";
import { DataNilaiRaportSmbjm, PaginationData } from "@/types";
import { LuCheck } from "react-icons/lu";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import FormVerifikasi from "./FormVerifikasi";
import { Pagination } from "./Pagination";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  paginationData: PaginationData<DataNilaiRaportSmbjm[]>;
  verifikatorKe: number;
  isLoading?: boolean; // Prop baru untuk handle status loading
}

export function DataTable<TData, TValue>({
  columns,
  data,
  paginationData,
  verifikatorKe,
  isLoading = false,
}: DataTableProps<TData & DataNilaiRaportSmbjm, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [showVerifikasi, setShowVerifikasi] = useState(false);
  const [selectedIdDataPendaftaran, setSelectedIdDataPendaftaran] =
    useState<String | null>(null);

  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || "",
  );
  const statusVerifikasiParams = searchParams.get("status-verifikasi") || "";

  const debouncedQuery = useDebounce(searchQuery, 1000);
  const navigate = useNavigate();
  const location = useLocation();

  const isVerifikatorKetiga = verifikatorKe == 3;

  const visibleColumns = React.useMemo(() => {
    if (isVerifikatorKetiga)
      return columns.filter(
        (col) =>
          (col as any).id !== "jml_belum_cek" &&
          (col as any).accessorKey !== "jml_belum_cek",
      );
    return columns.filter(
      (col) =>
        (col as any).id !== "status_rekon" &&
        (col as any).accessorKey !== "status_rekon",
    );
  }, [columns, isVerifikatorKetiga]);

  const table = useReactTable({
    data,
    columns: visibleColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (debouncedQuery) {
      if (
        params.get("search") !== debouncedQuery ||
        params.get("page") !== "1"
      ) {
        params.set("search", debouncedQuery);
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    } else {
      if (params.has("search") || params.get("page") !== "1") {
        params.delete("search");
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    }
  }, [debouncedQuery, navigate, location.pathname, location.search]);

  const currentTab = statusVerifikasiParams || (isVerifikatorKetiga ? "rekon" : "semua");

  // Total span kolom untuk handling state empty & loading
  const columnSpanLength = isVerifikatorKetiga
    ? columns.length + 1
    : columns.length;

  const handleTabChange = (value: string) => {
    if (value === "semua") {
      searchParams.delete("status-verifikasi");
    } else {
      searchParams.set("status-verifikasi", value);
    }

    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div className="flex-1 flex-wrap flex items-center gap-3">
          <Input
            autoFocus
            placeholder="Filter by nomor pendaftaran atau nama lengkap"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="max-w-sm"
          />

          <Tabs
            value={currentTab}
            onValueChange={handleTabChange}
            className="w-full sm:w-auto"
          >
            <TabsList className="grid w-full grid-cols-2 lg:flex lg:w-auto h-auto p-1 rounded-full">
              <TabsTrigger value="semua" className="rounded-full px-4 py-2">
                Semua
              </TabsTrigger>

              {isVerifikatorKetiga ? (
                <TabsTrigger value="rekon" className="rounded-full px-4 py-2">
                  Rekon
                </TabsTrigger>
              ) : (
                <>
                  <TabsTrigger
                    value="sudah-diverifikasi"
                    className="rounded-full px-4 py-2"
                  >
                    Sudah diverifikasi
                  </TabsTrigger>

                  <TabsTrigger
                    value="belum-diverifikasi"
                    className="rounded-full px-4 py-2"
                  >
                    Belum diverifikasi
                  </TabsTrigger>
                </>
              )}
            </TabsList>
          </Tabs>
        </div>
        <div className="flex gap-x-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="border rounded-xl relative overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  return (
                    <React.Fragment key={header.id}>
                      {index == 3 && isVerifikatorKetiga && (
                        <TableHead className="whitespace-nowrap">
                          Jumlah Nilai Rekon
                        </TableHead>
                      )}
                      <TableHead>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    </React.Fragment>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // UX: Tampilkan Skeleton baris jika sedang Loading
              Array.from({ length: 5 }).map((_, rowIndex) => (
                <TableRow key={`skeleton-row-${rowIndex}`}>
                  {Array.from({ length: columnSpanLength }).map(
                    (_, colIndex) => (
                      <TableCell
                        key={`skeleton-col-${colIndex}`}
                        className="py-4"
                      >
                        <div className="h-5 bg-slate-200 dark:bg-slate-800 rounded-md w-full animate-pulse"></div>
                      </TableCell>
                    ),
                  )}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell, index) => {
                    const isDone = row.original?.jml_belum_cek == "0";

                    if (index == 1)
                      return (
                        <TableCell key={cell.id}>
                          <Button
                            variant={isDone ? "ghost" : "dark"}
                            className="flex gap-3"
                            onClick={() => {
                              setShowVerifikasi(true);
                              setSelectedIdDataPendaftaran(
                                (row.original?.id_data_pendaftaran as string) ||
                                  "",
                              );
                            }}
                          >
                            Verifikasi <LuCheck />
                          </Button>
                        </TableCell>
                      );

                    return (
                      <React.Fragment key={cell.id}>
                        {index == 3 && isVerifikatorKetiga && (
                          <TableCell className="whitespace-nowrap">
                            <Badge variant={"secondary"}>
                              {row.original.jml_nilai_rekon} Nilai
                            </Badge>
                          </TableCell>
                        )}
                        <TableCell>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columnSpanLength}
                  className="h-24 text-center text-slate-500 italic"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <br />
      <div>
        <Pagination table={table} paginationData={paginationData} />
      </div>

      <Modal
        show={showVerifikasi}
        onClose={() => setShowVerifikasi(false)}
        className="w-[95vw] h-[95vh]"
        closeable={false}
      >
        <FormVerifikasi
          id_data_pendaftaran={selectedIdDataPendaftaran}
          onClose={() => setShowVerifikasi(false)}
        />
      </Modal>
    </div>
  );
}
